import { PlanningActionMetadataDtoModelType, PlanningActionsStatus } from '../../../models';
import * as MoveRouteNotifications from '../../../../../notifications/routes/moveRoute';
import getAppEnv from '../../../utils/getAppEnv';

// The bulk + single route move reuse the existing UpdateRouteConstraints action (planning-service),
// so we can't tell a move apart from any other constraint edit (e.g. RouteForm) from the payload alone.
// We react when the move flow has an `isMovingRoutes` intent set. The move records the action's UUID
// once the mutation resolves; matching on it prevents an unrelated UpdateRouteConstraints edit from
// stealing the move notification. Before the UUID is reconciled (the brief window between firing the
// mutation and its response, where a fast terminal event could arrive first) we claim on the pending
// intent so the move toast is never dropped.
const UPDATE_ROUTE_CONSTRAINTS_ACTION = 'UpdateRouteConstraints';

// Invoked by defaultHandler (the move has no dedicated subscription DTO; it arrives as the generic
// PlanningActionMetadataDto). Returns true when it owns the action so defaultHandler can short-circuit
// its generic success/failure notifications and avoid a duplicate toast.
const handleMoveRoutesNotification = (
  action: PlanningActionMetadataDtoModelType,
  appStore: any,
  projectId: string,
): boolean => {
  const { ui } = appStore;
  const moving = ui.isMovingRoutes;

  if (
    action.actionName !== UPDATE_ROUTE_CONSTRAINTS_ACTION
    || !moving
    || (moving.actionUuid && moving.actionUuid !== action.actionUuid)
  ) {
    return false;
  }

  const count = moving.routeCount ?? 1;
  const targetDate = moving.targetDate ?? undefined;
  const selectedDate = ui.selectedDate ?? undefined;

  if (action.status === PlanningActionsStatus.FINISHED) {
    ui.setIsMovingRoutes(null);
    const navigateToTargetDate = targetDate
      ? () => {
        ui.selectRoute(null);
        if (selectedDate !== targetDate) {
          ui.selectDate(targetDate);
        }
      }
      : undefined;
    getAppEnv(action.store).notification(MoveRouteNotifications.moveRouteSuccessNotification(
      projectId,
      count,
      navigateToTargetDate,
      targetDate,
      selectedDate,
    ));
    return true;
  }

  if (action.status === PlanningActionsStatus.FAILED || action.status === PlanningActionsStatus.FINISHED_WITH_FAILED) {
    ui.setIsMovingRoutes(null);
    getAppEnv(action.store).notification(MoveRouteNotifications.moveRouteFailureNotification(
      projectId,
      action.output?.error,
      count,
    ));
    return true;
  }

  return false;
};

export default handleMoveRoutesNotification;
